import type { Holding } from "@/data/fund-detail-types";
import { downloadCsv, rowsToCsv } from "@/lib/csv-download";
import { useCallback, useRef, useState } from "react";
import { FundHoldingsSidePanel } from "./fund-holdings-side-panel";

export function FundHoldingsTable({
  holdings,
  holdingsCount,
  dateLabel,
  title = "Holdings",
  subtitle,
  showFounder,
  founderLookup,
  ticker,
  fundName,
  sectorBreakdownSlot,
}: {
  holdings: Holding[];
  holdingsCount: number;
  dateLabel: string;
  title?: string;
  subtitle?: string;
  showFounder?: boolean;
  founderLookup?: Record<string, string>;
  ticker?: string;
  fundName?: string;
  sectorBreakdownSlot?: React.ReactNode;
}) {
  const [panelOpen, setPanelOpen] = useState(false);
  const expandBtnRef = useRef<HTMLButtonElement>(null);
  const visible = holdings.slice(0, 10);
  const topPct = visible.reduce((sum, h) => sum + (h.pct ?? 0), 0);
  const maxPct = holdings[0]?.pct ?? 1;

  const closePanel = useCallback(() => {
    setPanelOpen(false);
    requestAnimationFrame(() => expandBtnRef.current?.focus());
  }, []);

  const handleDownload = useCallback(() => {
    if (holdings.length === 0) return;
    const slug = (ticker ?? "fund").toLowerCase();
    const dateForFile = (dateLabel || "").replace(/[^A-Za-z0-9]+/g, "-");
    const filename = `${slug}-holdings${dateForFile ? `-${dateForFile}` : ""}.csv`;
    const csv = rowsToCsv(holdings, [
      { header: "Name", value: (h: Holding) => h.name },
      { header: "Ticker", value: (h: Holding) => h.ticker },
      {
        header: "Weight (%)",
        value: (h: Holding) => (h.pct == null ? "" : h.pct.toFixed(2)),
      },
      { header: "Market Value", value: (h: Holding) => h.marketValue ?? "" },
      { header: "CUSIP", value: (h: Holding) => h.cusip ?? "" },
    ]);
    downloadCsv(filename, csv);
  }, [holdings, dateLabel, ticker]);

  const gridCols = showFounder
    ? "grid-cols-[32px_1fr_120px_116px_64px_100px_96px] max-md:grid-cols-[28px_minmax(0,1fr)_64px] max-md:gap-2.5"
    : "grid-cols-[32px_1fr_96px_116px_64px_96px] max-md:grid-cols-[28px_minmax(0,1fr)_64px] max-md:gap-2.5";

  return (
    <div className="border-b border-white/[0.13]">
      <div className="flex items-center justify-between gap-3 px-10 py-6 border-b border-white/[0.13] max-md:px-4 max-md:py-4 max-md:flex-col max-md:items-start">
        <div className="font-[family-name:var(--font-f37bolton)] text-[24px] text-[var(--etf-white)] tracking-[-0.02em] leading-none flex items-center gap-3 max-md:text-[20px]">
          <span className="inline-block w-[3px] h-[20px] bg-[var(--etf-orange)]" />
          {title}
        </div>
        <div className="font-[family-name:var(--font-geist)] text-[12px] text-[var(--etf-white3)] tracking-[0.08em]">
          {subtitle && <span className="text-[var(--etf-orange)] mr-1.5">{subtitle} &middot;</span>}
          Top {Math.min(visible.length, 10)} of {holdingsCount} total (subject to change) &middot;
          As of {dateLabel}
        </div>
      </div>

      {sectorBreakdownSlot}

      <div
        className={`grid ${gridCols} gap-4 items-center px-10 py-4 border-b border-white/[0.13] opacity-50 pointer-events-none max-md:px-4`}
      >
        <span className="font-[family-name:var(--font-instrument)] text-[9px] text-[var(--etf-white3)]">
          #
        </span>
        <span className="font-[family-name:var(--font-instrument)] text-[12px] leading-[1.5] tracking-[0.1em] text-[var(--etf-white3)] uppercase">
          Company
        </span>
        <span className="font-[family-name:var(--font-instrument)] text-[12px] leading-[1.5] tracking-[0.1em] text-[var(--etf-white3)] uppercase pl-4 max-md:hidden">
          Weight
        </span>
        <span className="font-[family-name:var(--font-instrument)] text-[12px] leading-[1.5] tracking-[0.1em] text-[var(--etf-white3)] uppercase whitespace-nowrap max-md:hidden">
          Market Value
        </span>
        <span className="font-[family-name:var(--font-instrument)] text-[12px] leading-[1.5] tracking-[0.1em] text-[var(--etf-white3)] uppercase text-right pr-4 whitespace-nowrap">
          Wgt %
        </span>
        {showFounder && (
          <span className="font-[family-name:var(--font-instrument)] text-[12px] leading-[1.5] tracking-[0.1em] text-[var(--etf-white3)] uppercase text-right max-md:hidden">
            Founder
          </span>
        )}
        <span className="font-[family-name:var(--font-instrument)] text-[12px] leading-[1.5] tracking-[0.1em] text-[var(--etf-white3)] uppercase max-md:hidden">
          CUSIP
        </span>
      </div>

      {visible.map((holding, i) => (
        <HoldingRow
          key={holding.ticker}
          holding={holding}
          index={i}
          maxPct={maxPct}
          showFounder={showFounder}
          gridCols={gridCols}
          founderName={holding.founder ?? founderLookup?.[holding.ticker.toUpperCase()]}
        />
      ))}

      <div className="flex items-center justify-between gap-3 px-10 py-4 border-t border-white/[0.13] flex-wrap max-md:px-4">
        <span className="font-[family-name:var(--font-instrument)] text-sm text-[var(--etf-white2)] tracking-[0.04em] max-md:text-[12px]">
          Top 10 = {topPct.toFixed(2)}% of fund&nbsp;&middot;&nbsp;{holdingsCount} total holdings
          (subject to change)
        </span>
        <div className="flex items-center gap-4 max-md:gap-3 flex-wrap">
          <button
            type="button"
            className="inline-flex items-center gap-[5px] py-[5px] pr-[13px] pl-[11px] border-[1.5px] border-[var(--etf-orange)] rounded-full text-[var(--etf-orange)] font-[family-name:var(--font-instrument)] text-[11px] cursor-pointer bg-transparent whitespace-nowrap tracking-[0.04em] uppercase transition-colors duration-[250ms] relative overflow-hidden disabled:opacity-40 disabled:cursor-not-allowed hover:bg-[rgba(255,85,0,0.06)]"
            onClick={handleDownload}
            disabled={holdings.length === 0}
            aria-label="Download holdings data as CSV"
          >
            <span className="relative z-[1]" aria-hidden="true">
              &#8595;
            </span>
            <span className="relative z-[1]">Download Holdings</span>
          </button>
          {holdingsCount > 10 && (
            <button
              type="button"
              ref={expandBtnRef}
              onClick={() => setPanelOpen(true)}
              aria-haspopup="dialog"
              aria-expanded={panelOpen}
              className="font-[family-name:var(--font-instrument)] text-sm text-[var(--etf-orange)] hover:opacity-80 transition-colors cursor-pointer bg-transparent border-0"
            >
              View All Holdings &rarr;
            </button>
          )}
        </div>
      </div>

      <FundHoldingsSidePanel
        open={panelOpen}
        onClose={closePanel}
        holdings={holdings}
        holdingsCount={holdingsCount}
        dateLabel={dateLabel}
        ticker={ticker ?? ""}
        fundName={fundName}
        showFounder={showFounder}
        founderLookup={founderLookup}
      />
    </div>
  );
}

function HoldingRow({
  holding,
  index,
  maxPct,
  showFounder,
  gridCols,
  founderName,
}: {
  holding: Holding;
  index: number;
  maxPct: number;
  showFounder?: boolean;
  gridCols: string;
  founderName?: string;
}) {
  const barWidth = ((holding.pct ?? 0) / maxPct) * 100;

  return (
    <div
      className={`grid ${gridCols} gap-4 items-center px-10 py-4 border-b border-white/[0.08] hover:bg-white/[0.02] transition-colors max-md:px-4 max-md:py-3.5`}
    >
      <span className="font-[family-name:var(--font-instrument)] text-[14px] leading-[1.5] text-[var(--etf-white2)] tabular-nums">
        {String(index + 1).padStart(2, "0")}
      </span>

      <div className="min-w-0">
        <div className="font-[family-name:var(--font-instrument)] text-[14px] leading-[1.5] font-medium text-[var(--etf-orange)] mb-0.5">
          {holding.ticker}
        </div>
        <div className="font-[family-name:var(--font-instrument)] text-[14px] leading-[1.5] text-[var(--etf-white2)] max-md:text-[13px] max-md:truncate">
          {holding.name}
        </div>
      </div>

      <div className="pl-4 pr-4 max-md:hidden">
        <div className="h-1 rounded bg-white/[0.05] overflow-hidden">
          <div
            className="h-full bg-[linear-gradient(90deg,var(--etf-orange),color-mix(in_srgb,var(--etf-orange)_70%,white))] rounded-sm shadow-[0_0_8px_rgba(255,85,0,0.4)] animate-[bar-fill-in_0.8s_ease_0.3s_both]"
            style={{ "--target-width": `${barWidth}%` } as React.CSSProperties}
          />
        </div>
      </div>

      <div className="font-[family-name:var(--font-instrument)] text-[13px] text-[var(--etf-white2)] tabular-nums pl-10 max-md:hidden">
        {holding.marketValue}
      </div>

      <div className="font-[family-name:var(--font-instrument)] text-[14px] leading-[1.5] text-[var(--etf-white2)] text-right tabular-nums pr-4">
        {holding.pct == null ? "—" : `${holding.pct.toFixed(2)}%`}
      </div>

      {showFounder && (
        <div className="font-[family-name:var(--font-instrument)] text-[11px] text-[var(--etf-white3)] text-right max-md:hidden">
          {founderName || "—"}
        </div>
      )}

      <div className="font-[family-name:var(--font-instrument)] text-[12px] text-[var(--etf-white3)] tabular-nums max-md:hidden">
        {holding.cusip || "—"}
      </div>
    </div>
  );
}
